import "./tenants.css";

import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";

import AlertDialog from "../../components/utils/alertDialog";
import { SessionHelper } from "../../utils/index";

const FETCH_TENANTS_QUERY = gql`
  query {
    tenants {
      id
      domainName
      domainId
      channels
      concurrentUser
      concurrentSessions
      onHoldUsers
      isActive
      adminNotification
    }
  }
`;

function getChannelCount(channels) {
  if (Array.isArray(channels)) return channels.length;
  if (typeof channels === "number") return channels;
  return "—";
}

function formatNumber(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (isNaN(n)) return "—";
  return n.toLocaleString();
}

// ── SVG icons ──────────────────────────────────────────────
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
);

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const ExportIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const EmptyIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    <line x1="12" y1="12" x2="12" y2="16" /><line x1="10" y1="14" x2="14" y2="14" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

// ── Dropdown portal ────────────────────────────────────────
function DropdownMenu({ pos, onClose, children }) {
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className="dropdown-menu"
      style={{
        position: "fixed",
        top: pos.top,
        right: pos.right,
        zIndex: 9999,
      }}
    >
      {children}
    </div>,
    document.body
  );
}

// ── Main component ─────────────────────────────────────────
function TenantsList() {
  const { data = {}, loading } = useQuery(FETCH_TENANTS_QUERY, {
    fetchPolicy: "network-only",
  });

  const [alertOpen, setAlertOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  const history = useHistory();
  const currentUsername = SessionHelper?.currentUser?.username;
  const canEdit = currentUsername !== "ICQ24";

  const allTenants = data.tenants || [];

  const filteredTenants = allTenants.filter((t) => {
    const q = searchQuery.toLowerCase();
    return (
      (t.domainName || "").toLowerCase().includes(q) ||
      (t.domainId || "").toLowerCase().includes(q)
    );
  });

  const totalCount = filteredTenants.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  const paginatedTenants = filteredTenants.slice(
    safePage * rowsPerPage,
    (safePage + 1) * rowsPerPage
  );

  const handleMenuOpen = useCallback((id, e) => {
    e.stopPropagation();
    if (openMenuId === id) {
      setOpenMenuId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
    setOpenMenuId(id);
  }, [openMenuId]);

  const closeMenu = useCallback(() => setOpenMenuId(null), []);

  const handleEdit = (tenant) => {
    closeMenu();
    history.push(`/edit-tenants/${tenant.id}`);
  };

  const handleViewDetails = (tenant) => {
    closeMenu();
    history.push(`/edit-tenants/${tenant.id}`);
  };

  const handleDeleteClick = () => {
    closeMenu();
    setAlertOpen(true);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleExport = () => {
    const headers = [
      "Domain Name",
      "Domain ID",
      "Channels",
      "Concurrent Users",
      "Concurrent Sessions",
      "Hold Sessions",
      "Status",
    ];
    const rows = filteredTenants.map((t) => [
      t.domainName || "",
      t.domainId || "",
      getChannelCount(t.channels),
      t.concurrentUser ?? "",
      t.concurrentSessions ?? "",
      t.onHoldUsers ?? "",
      t.isActive ? "Active" : "Inactive",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${v}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tenants.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeTenant = paginatedTenants.find((t) => t.id === openMenuId);

  const firstRow = safePage * rowsPerPage + 1;
  const lastRow = Math.min((safePage + 1) * rowsPerPage, totalCount);

  return (
    <div className="tenants-page">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-title">Tenants</h1>
          <p className="page-subtitle">Manage tenants across your organization.</p>
        </div>
        <button className="btn-primary" onClick={() => history.push("/add-tenants")}>
          + New Tenant
        </button>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <div className="search-container">
            <span className="search-icon">
              <SearchIcon />
            </span>
            <input
              className="search-input"
              type="text"
              placeholder="Search by tenant name or domain ID..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <button className="btn-outline">
            <FilterIcon />
            Filter
          </button>
          <button className="btn-outline" onClick={handleExport}>
            <ExportIcon />
            Export
          </button>
        </div>
        <span className="tenant-count">{allTenants.length} Tenants</span>
      </div>

      {/* Table card */}
      <div className="table-card">
        {loading ? (
          <div className="loading-state">
            <span className="loading-spinner" />
            Loading tenants…
          </div>
        ) : allTenants.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state-icon">
              <EmptyIcon />
            </span>
            <h3 className="empty-state-title">No tenants found</h3>
            <p className="empty-state-desc">
              Create your first tenant to start managing your organization.
            </p>
            <button className="btn-primary" onClick={() => history.push("/add-tenants")}>
              New Tenant
            </button>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="tenants-table">
                <thead>
                  <tr>
                    <th>Domain Name</th>
                    <th>Domain ID</th>
                    <th className="col-numeric">Channels</th>
                    <th className="col-numeric">Concurrent Users</th>
                    <th className="col-numeric">Concurrent Sessions</th>
                    <th className="col-numeric">Hold Sessions</th>
                    <th>Status</th>
                    <th className="actions-cell" />
                  </tr>
                </thead>
                <tbody>
                  {paginatedTenants.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        style={{
                          textAlign: "center",
                          padding: "48px",
                          color: "#9ca3af",
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        No results for &ldquo;{searchQuery}&rdquo;
                      </td>
                    </tr>
                  ) : (
                    paginatedTenants.map((tenant) => (
                      <tr key={tenant.id}>
                        <td>
                          <span className="domain-name">{tenant.domainName}</span>
                        </td>
                        <td>
                          <span className="domain-id-badge">{tenant.domainId}</span>
                        </td>
                        <td className="numeric-cell">
                          {getChannelCount(tenant.channels)}
                        </td>
                        <td className="numeric-cell">
                          {formatNumber(tenant.concurrentUser)}
                        </td>
                        <td className="numeric-cell">
                          {formatNumber(tenant.concurrentSessions)}
                        </td>
                        <td className="numeric-cell">
                          {tenant.onHoldUsers != null ? tenant.onHoldUsers : "—"}
                        </td>
                        <td>
                          <span
                            className={`status-pill ${
                              tenant.isActive ? "status-active" : "status-inactive"
                            }`}
                          >
                            <span className="status-dot" />
                            {tenant.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <div className="actions-menu-wrapper">
                            <button
                              className="menu-trigger"
                              onClick={(e) => handleMenuOpen(tenant.id, e)}
                              aria-label="Open actions"
                            >
                              <MoreIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <div className="rows-per-page">
                <span>Rows per page</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </select>
              </div>
              <div className="pagination-controls">
                <span className="pagination-info">
                  {totalCount === 0 ? "0" : `${firstRow}–${lastRow}`} of {totalCount}
                </span>
                <button
                  className="pagination-btn"
                  disabled={safePage === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon />
                </button>
                <span className="pagination-current">
                  {safePage + 1} / {pageCount}
                </span>
                <button
                  className="pagination-btn"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage((p) => p + 1)}
                  aria-label="Next page"
                >
                  <ChevronRightIcon />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Dropdown menu portal */}
      {openMenuId !== null && activeTenant && (
        <DropdownMenu pos={menuPos} onClose={closeMenu}>
          {canEdit && (
            <button className="dropdown-item" onClick={() => handleEdit(activeTenant)}>
              Edit Tenant
            </button>
          )}
          <button className="dropdown-item" onClick={() => handleViewDetails(activeTenant)}>
            View Details
          </button>
          <div className="dropdown-divider" />
          <button className="dropdown-item dropdown-item-danger" onClick={handleDeleteClick}>
            Delete Tenant
          </button>
        </DropdownMenu>
      )}

      <AlertDialog open={alertOpen} alertOption={() => setAlertOpen(false)} />
    </div>
  );
}

export default TenantsList;
